import { useRef, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Paperclip, ShieldAlert, ShieldCheck, ShieldQuestion, Upload } from 'lucide-react';
import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { formatarDataHora, formatarTamanho, mensagemDeErro } from '@/lib/format';
import { NomeDePessoa, useDicionarioDePessoas } from '@/features/tickets/pessoas';
import { baixarAnexo, chaves, enviarAnexo, listarAnexos } from '@/features/tickets/tickets.api';
import { ROTULO_DE_VARREDURA, type Anexo, type StatusDeVarredura } from '@/features/tickets/types';

/**
 * Anexos do chamado.
 *
 * O download só é oferecido para o que o antivírus liberou. Enquanto a
 * varredura não termina, o arquivo aparece — esconder daria a impressão de que
 * o envio falhou — mas sem botão: o servidor recusaria assim mesmo, e um botão
 * que sempre dá erro é pior do que botão nenhum.
 */
export function AttachmentsPanel({ ticketId }: { ticketId: string }) {
  const clienteDeConsulta = useQueryClient();
  const seletor = useRef<HTMLInputElement>(null);
  const [erroDeDownload, setErroDeDownload] = useState<string | null>(null);

  const anexos = useQuery({
    queryKey: chaves.anexos(ticketId),
    queryFn: () => listarAnexos(ticketId),
  });

  // Quem enviou cada arquivo, resolvido de uma vez para a lista inteira.
  const pessoas = useDicionarioDePessoas(anexos.data?.map((anexo) => anexo.uploadedById) ?? []);

  const enviar = useMutation<Anexo, Error, File>({
    mutationFn: (arquivo) => enviarAnexo(ticketId, arquivo),
    onSuccess: () => {
      void clienteDeConsulta.invalidateQueries({ queryKey: chaves.anexos(ticketId) });
    },
  });

  const baixar = useMutation<void, Error, Anexo>({
    mutationFn: (anexo) => baixarAnexo(ticketId, anexo),
    onError: (erro) => setErroDeDownload(mensagemDeErro(erro)),
  });

  function escolher(evento: ChangeEvent<HTMLInputElement>) {
    const arquivo = evento.target.files?.[0];
    if (!arquivo) return;
    enviar.mutate(arquivo);
    // Zera o campo para que reenviar o mesmo arquivo dispare `change` de novo.
    evento.target.value = '';
  }

  const erro = mensagemDeErro(enviar.error) ?? erroDeDownload;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Todo arquivo passa por varredura antes de ficar disponível para download.
        </p>
        <input ref={seletor} type="file" onChange={escolher} className="sr-only" />
        <Button
          size="sm"
          variant="secondary"
          loading={enviar.isPending}
          onClick={() => seletor.current?.click()}
          icon={<Upload className="h-4 w-4" aria-hidden />}
        >
          Anexar arquivo
        </Button>
      </div>

      {erro ? (
        <p role="alert" className="text-sm text-destructive">
          {erro}
        </p>
      ) : null}

      {anexos.isPending ? (
        <p className="text-sm text-muted-foreground">Carregando anexos…</p>
      ) : anexos.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {mensagemDeErro(anexos.error)}
        </p>
      ) : anexos.data.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border px-5 py-10 text-center">
          <Paperclip className="h-5 w-5 text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">Nenhum arquivo anexado.</p>
        </div>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
          {anexos.data.map((anexo) => (
            <li
              key={anexo.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-background px-4 py-2.5 text-sm"
            >
              <Paperclip className="h-4 w-4 flex-none text-muted-foreground" aria-hidden />
              <span className="min-w-0 flex-1 truncate font-medium">{anexo.fileName}</span>
              <span className="whitespace-nowrap text-xs text-muted-foreground">
                {formatarTamanho(anexo.sizeBytes)}
              </span>
              <span className="whitespace-nowrap text-xs text-muted-foreground">
                enviado por{' '}
                <NomeDePessoa
                  id={anexo.uploadedById}
                  pessoas={pessoas}
                  semId="sem autor registrado"
                  className="text-foreground"
                />
              </span>
              <span
                className="whitespace-nowrap text-xs text-muted-foreground"
                title={formatarDataHora(anexo.createdAt)}
              >
                {formatarDataHora(anexo.createdAt)}
              </span>
              <SeloDeVarredura status={anexo.scanStatus} />
              <Button
                size="sm"
                variant="ghost"
                disabled={anexo.scanStatus !== 'CLEAN'}
                loading={baixar.isPending && baixar.variables?.id === anexo.id}
                onClick={() => {
                  setErroDeDownload(null);
                  baixar.mutate(anexo);
                }}
                icon={<Download className="h-4 w-4" aria-hidden />}
              >
                Baixar
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const ICONE_DE_VARREDURA: Record<StatusDeVarredura, typeof ShieldCheck> = {
  PENDING: ShieldQuestion,
  CLEAN: ShieldCheck,
  INFECTED: ShieldAlert,
  FAILED: ShieldAlert,
};

const TOM_DE_VARREDURA: Record<StatusDeVarredura, string> = {
  PENDING: 'text-muted-foreground',
  CLEAN: 'text-success',
  INFECTED: 'text-destructive',
  FAILED: 'text-warning',
};

function SeloDeVarredura({ status }: { status: StatusDeVarredura }) {
  const Icone = ICONE_DE_VARREDURA[status];
  return (
    <span
      className={cn('inline-flex items-center gap-1.5 whitespace-nowrap text-xs', TOM_DE_VARREDURA[status])}
    >
      <Icone className="h-3.5 w-3.5" aria-hidden />
      {ROTULO_DE_VARREDURA[status]}
    </span>
  );
}
